import { FunctionComponent, useEffect, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import InputsNew from "../common/InputsNew";
import SearchableInputsDropdown from "../common/SearchableDropdown";
import { showError, showSuccess } from "../common/ToastNotification";
import { AGENCY_PROGRESS_STATUS, EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { CREATE_MIGRATION_AGENCY } from "../../api/mutation";
import Loader from "../common/Loader";
import { useAuth } from "../../context/AuthContext";

interface AddAgencyModalProps {
    onClose: () => void;
    onSuccess: () => void;
}

interface FormValues {
    agency_name: string;
    new_agency_id: string;
    old_agency_id?: string;
    total_clients: number;
    total_caregivers: number;
    total_employees: number;
    total_prospects?: number;
    verification_working_days?: number;
    onboarding_date?: string;
    data_migration_date?: string;
    verification_start_date?: string;
}

const schema = yup.object().shape({
    agency_name: yup.string()
        .test("no-whitespace", "This field is required", v => !!v && v.trim().length > 0)
        .required("This field is required"),
    new_agency_id: yup.string().required("This field is required"),
    old_agency_id: yup.string().nullable(),
    total_clients: yup.number()
        .typeError("Must be a number")
        .min(0, "Must be 0 or more")
        .required("This field is required"),
    total_caregivers: yup.number()
        .typeError("Must be a number")
        .min(0, "Must be 0 or more")
        .required("This field is required"),
    total_employees: yup.number()
        .typeError("Must be a number")
        .min(0, "Must be 0 or more")
        .required("This field is required"),
    total_prospects: yup.number()
        .typeError("Must be a number")
        .min(0, "Must be 0 or more")
        .nullable(),
    verification_working_days: yup.number()
        .typeError("Must be a number")
        .min(0, "Must be 0 or more")
        .required("This field is required"),
    onboarding_date: yup.string().nullable(),
    data_migration_date: yup.string().nullable(),
    verification_start_date: yup.string().nullable(),
});

const AddAgencyModal: FunctionComponent<AddAgencyModalProps> = ({ onClose, onSuccess }) => {
    const { userInfo } = useAuth();
    const organizationID = userInfo?.agencies?.id || 0;

    const [progressStatus, setProgressStatus] = useState<{ key: string; name: string }[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>("");
    const [statusError, setStatusError] = useState<string>("");
    const [cxMember1, setCxMember1] = useState<string>("");
    const [cxMember1Label, setCxMember1Label] = useState<string>("");
    const [cxMember2, setCxMember2] = useState<string>("");
    const [cxMember2Label, setCxMember2Label] = useState<string>("");
    const [agencyType, setAgencyType] = useState<string>("");
    const [agencyTypeError, setAgencyTypeError] = useState<string>("");

    const { data: statusData, loading: statusLoading } = useQuery(AGENCY_PROGRESS_STATUS, {
        fetchPolicy: "no-cache",
    });

    const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
        variables: { agency_id: parseInt(organizationID) },
        fetchPolicy: "no-cache",
        skip: !organizationID,
    });

    const [createMigrationAgency, { loading: createLoading }] = useMutation(CREATE_MIGRATION_AGENCY, {
        fetchPolicy: "no-cache",
    });

    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(schema) as any,
    });

    useEffect(() => {
        if (statusData?.agency_progress_statuses) {
            setProgressStatus(
                statusData.agency_progress_statuses.map((s: any) => ({
                    key: s.value,
                    name: s.label,
                }))
            );
        }
    }, [statusData]);

    const employeeOptions = (employeesData?.employees_by_agency_id?.employees || []).map((e: any) => ({
        key: String(e.employee_id),
        name: `${e.first_name} ${e.last_name}`.trim(),
    }));

    const onSubmit = async (data: FormValues) => {
        if (!agencyType) {
            setAgencyTypeError("Agency type is required");
            return;
        }
        try {
            const input: any = {
                agency_name: data.agency_name,
                new_agency_id: parseInt(data.new_agency_id),
                total_clients: data.total_clients,
                total_caregivers: data.total_caregivers,
                total_employees: data.total_employees,
                verification_working_days: data.verification_working_days,
                agency_type: agencyType,
            };
            if (data.old_agency_id) input.old_agency_id = parseInt(data.old_agency_id);
            if (data.total_prospects != null) input.total_prospects = data.total_prospects;
            if (data.onboarding_date) input.onboarding_date = data.onboarding_date;
            if (data.data_migration_date) input.data_migration_date = data.data_migration_date;
            if (data.verification_start_date) input.verification_start_date = data.verification_start_date;
            if (selectedStatus) input.progress_status = selectedStatus;
            if (cxMember1) input.cx_member = parseInt(cxMember1);
            if (cxMember2) input.cx_member_2 = parseInt(cxMember2);

            const res = await createMigrationAgency({ variables: { input } });
            const result = res.data?.create_migration_agency;
            if (result?.status_code === 201 || result?.status_code === 200) {
                showSuccess(result.message || "Agency added successfully");
                onSuccess();
            } else {
                showError(result?.message || "Something went wrong");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center agency-modelpopup">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-[640px] mx-4 max-h-[70vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                    <h4 className="text-base font-bold text-gray-900 dark:text-white m-0">Add Agency</h4>
                    <button onClick={onClose} className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6 flex flex-col gap-4">
                    {statusLoading ? <Loader /> : (
                    <>
                    <div className="grid grid-cols-2 mq575:grid-cols-1 gap-4">
                        <InputsNew
                            label="Agency Name*"
                            contentPlaceholder="Enter agency name"
                            type="text"
                            register={register("agency_name")}
                            errorMessage={errors.agency_name}
                        />
                        <InputsNew
                            label="New Agency ID*"
                            contentPlaceholder="Enter new agency ID"
                            type="number"
                            register={register("new_agency_id")}
                            errorMessage={errors.new_agency_id}
                        />
                        <InputsNew
                            label="Old Agency ID"
                            contentPlaceholder="Enter old agency ID"
                            type="number"
                            register={register("old_agency_id")}
                            errorMessage={errors.old_agency_id}
                        />
                        <InputsNew
                            label="Total Clients*"
                            contentPlaceholder="0"
                            type="number"
                            register={register("total_clients")}
                            errorMessage={errors.total_clients}
                        />
                        <InputsNew
                            label="Total Caregivers*"
                            contentPlaceholder="0"
                            type="number"
                            register={register("total_caregivers")}
                            errorMessage={errors.total_caregivers}
                        />
                        <InputsNew
                            label="Total Employees*"
                            contentPlaceholder="0"
                            type="number"
                            register={register("total_employees")}
                            errorMessage={errors.total_employees}
                        />
                        <InputsNew
                            label="Total Prospects"
                            contentPlaceholder="0"
                            type="number"
                            register={register("total_prospects")}
                            errorMessage={errors.total_prospects}
                        />
                        <InputsNew
                            label="Data Migration Date"
                            contentPlaceholder=""
                            type="date"
                            register={register("data_migration_date")}
                            errorMessage={errors.data_migration_date}
                        />
                        <InputsNew
                            label="Onboarding Date"
                            contentPlaceholder=""
                            type="date"
                            register={register("onboarding_date")}
                            errorMessage={errors.onboarding_date}
                        />
                        <InputsNew
                            label="Verification Start Date"
                            contentPlaceholder=""
                            type="date"
                            register={register("verification_start_date")}
                            errorMessage={errors.verification_start_date}
                        />
                        <InputsNew
                            label="Verification Working Days"
                            contentPlaceholder="0"
                            type="number"
                            register={register("verification_working_days")}
                            errorMessage={errors.verification_working_days}
                        />
                    </div>

                    {/* Agency Type Dropdown */}
                    <div className="flex flex-col gap-1">
                        <SearchableInputsDropdown
                            label="Agency Type*"
                            placeHolder="Select agency type"
                            options={[
                                { key: "home_care", name: "Home Care" },
                                { key: "case_management", name: "Case Management" },
                            ]}
                            initialValue={agencyType === "home_care" ? "Home Care" : agencyType === "case_management" ? "Case Management" : ""}
                            onSelect={(key: string) => {
                                setAgencyType(key);
                                setAgencyTypeError("");
                            }}
                            showSearchable={false}
                            dontselectlastValue={true}
                        />
                        {agencyTypeError && (
                            <p className="text-red-500 text-xs mt-1">{agencyTypeError}</p>
                        )}
                    </div>

                    {/* Progress Status Dropdown */}
                    <div className="flex flex-col gap-1">
                        <SearchableInputsDropdown
                            label="Progress Status"
                            placeHolder="Select status"
                            options={progressStatus}
                            initialValue={selectedStatusLabel}
                            onSelect={(key: string, name: string) => {
                                setSelectedStatus(key);
                                setSelectedStatusLabel(name);
                                setStatusError("");
                            }}
                            showSearchable={false}
                            dontselectlastValue={true}
                        />
                        {statusError && (
                            <p className="text-red-500 text-xs mt-1">{statusError}</p>
                        )}
                    </div>

                    {/* CS Member Dropdowns */}
                    <div className="grid grid-cols-2 mq575:grid-cols-1 gap-4">
                        <SearchableInputsDropdown
                            label="Customer Support 1"
                            placeHolder={employeesLoading ? "Loading..." : "Select member"}
                            options={employeeOptions}
                            initialValue={cxMember1Label}
                            onSelect={(key: string, name: string) => {
                                setCxMember1(key);
                                setCxMember1Label(name);
                            }}
                            showSearchable={true}
                            dontselectlastValue={true}
                        />
                        <SearchableInputsDropdown
                            label="Customer Support 2"
                            placeHolder={employeesLoading ? "Loading..." : "Select member"}
                            options={employeeOptions}
                            initialValue={cxMember2Label}
                            onSelect={(key: string, name: string) => {
                                setCxMember2(key);
                                setCxMember2Label(name);
                            }}
                            showSearchable={true}
                            dontselectlastValue={true}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createLoading}
                            className="px-6 py-2 plus-task-btn  text-white rounded-lg font-semibold text-sm disabled:opacity-60"
                        >
                            {createLoading ? "Saving..." : "Add Agency"}
                        </button>
                    </div>
                    </>
                    )}
                </form>
                {createLoading && (
                    <Loader />
                )}
            </div>
        </div>
    );
};

export default AddAgencyModal;
